import { useLocation } from 'react-router-dom'
import { ComponentWithPermissions } from '../../../../../baseComponents/CreateRoutering/types'
import useTwinTranslation from '../../../../../utils/hooks/useTwinTranslation'
import { TwinTableLink } from '../../../../../baseComponents/TwinTable'
import { BaseScreenTableSimple } from '../../../../../baseComponents/TwinTable/BaseScreenTable'
import { TableComponentProps } from '../../../../../baseComponents/TwinTable/BaseScreenTable/types'
import {  CustomRenderDate, CustomRenderString, EditRow } from '../../../../../baseComponents/TwinTable/VirtualTableListing/CustomRenders'
import { ColumnTableSchema } from '../../../../../baseComponents/TwinTable/VirtualTableListing/Subcomponents/types'
import { getBasePath } from '../../../../../utils/globals/link'
import { TwinDictionary } from '../../../../../utils/globals/dictionary'
import { VatRepercutedModelType } from '@teinor/erp/types/company/accounting/vatRepercuted'
import { DisplayStatus } from '../../../../../baseComponents/Displays/DisplayStatus'
import { faCheckCircle, faPencil } from '@fortawesome/pro-light-svg-icons'
import { WorkingCenterPayload } from '../../../../../utils/reducers/company/workingCenters'
import { connect, ConnectedProps } from 'react-redux'
import withLoading from '../../../../../utils/hoc/withLoading'
import { AllReduxPayloads } from '../../../../../utils/reducers'
import { CustomerPayload } from '../../../../../utils/reducers/customers/customer'

interface VatRepercutedProps extends ComponentWithPermissions { }

const VatRepercuted: React.FC<VatRepercutedProps> = ({ userPermissions }) => {
    const { t } = useTwinTranslation()
    const pageData = {
        title: t('vatRepercuted', 'IVA Repercutido'),
    }

    return (
        <BaseScreenTableSimple TableComponent={TableVatRepercutedConnect} pageData={pageData} userPermissions={userPermissions} />
    )
}

type TableVatRepercutedProps = TableComponentProps & ReduxVatRepercutedConnector & {}

const TableVatRepercuted: React.FC<TableVatRepercutedProps> = ({ setTableInstance, workingCenters, setOpened, customers }) => {
    const { t } = useTwinTranslation()
    const location = useLocation().pathname
    const vatRepercutedStatus: TwinDictionary = {
        0: { name: t('notProcessed', 'Sin procesar') },
        1: { name: t('processed', 'Procesado') }
    }
    const columns: ColumnTableSchema<VatRepercutedModelType> = [
        {
            id: 'full_assigned_number',
            dataKey: 'full_assigned_number',
            label: t('nAssigned', 'Nº Assignado'),
            sortable: true,
            width: 150,
            customRender: (parameterValue, allRowData) => <CustomRenderString value={parameterValue || allRowData.id} className={parameterValue ? '' : 'text-red-BA'} />,
        },
        {
            id: 'CustomerId',
            dataKey: 'CustomerId',
            label: t('customer', 'Cliente'),
            customRender: (parameterValue) => <CustomRenderString value={customers?.all?.[parameterValue as any]?.name || ''} />,
            searchableComponent: {
                component: 'SearchableComponentsCustomSelectSearchBar',
                extraComponentData: { items: customers?.all || {}, label: t('customer', 'Cliente') }
            },
        },
        {
            id: 'WorkingCenterId',
            dataKey: 'WorkingCenterId',
            label: t('billedFor', 'Facturado por'),
            customRender: (parameterValue) => <CustomRenderString value={workingCenters?.[parameterValue as any]?.name || ''} />,
            searchableComponent: {
                component: 'SearchableComponentsCustomSelectSearchBar',
                extraComponentData: { items: workingCenters || {}, label: t('workingCenter', 'Centro de trabajo') }
            },
            minWidth: 140
        },
        {
            id: 'status',
            dataKey: 'status',
            label: t('status', 'Estado'),
            sortable: true,
            customRender: (parameterValue) => <VatRepercutedStatus value={parameterValue} />,
            searchableComponent: {
                component: 'SearchableComponentsCustomSelect',
                extraComponentData: { items: vatRepercutedStatus, label: t('statusVatRepercuted', 'Estado de la compra') }
            },
            minWidth: 135
        },
        {
            id: 'processed_date',
            dataKey: 'processed_date',
            label: t('createdDate', 'Fecha de creación'),
            customRender: (parameterValue, allRowData) => <CustomRenderDate value={parameterValue || allRowData.createdAt} className={parameterValue ? '' : 'text-red-BA'} />,
            sortable: true,
            minWidth: 120
        },
        {
            id: 'finished_date',
            dataKey: 'finished_date',
            label: t('finishedDate', 'Fecha de finalización'),
            customRender: (parameterValue) => <CustomRenderDate value={parameterValue} />,
            sortable: true,
            minWidth: 120
        },
        {
            id: 'edit',
            dataKey: 'id',
            label: '',
            width: 50,
            customRender: (parameterValue, allRowData) => <EditRow key={'edit' + parameterValue} {...{ parameterValue, allRowData }} />
        }
    ]
    return (
        <TwinTableLink createRowDataLink={[getBasePath(location, 'vatRepercuted'), ['$id']]} name='vatRepercuted' columns={columns} getDataFrom='/api/app/accounting/vatRepercuted/getAllVatRepercuteds' onRowClick={(_id, rowData) => setOpened({
            type: 'edit',
            allRowData: rowData
        })} setTableInstance={setTableInstance} />
    )
}

interface VatRepercutedStatusProps {
    value: string
}

const VatRepercutedStatus: React.FC<VatRepercutedStatusProps> = ({ value }) => {
    const parsedValue = parseInt(value)
    const { t } = useTwinTranslation()
    if (parsedValue === 0) {
        return <DisplayStatus icon={faPencil} colorText='text-red-BA' text={t('draft', 'Borrador')} />
    } else if (parsedValue === 1) {
        return <DisplayStatus icon={faCheckCircle} colorText='text-green-43' text={t('processed', 'Procesado')} />
    }
    return null
}
const tableVatRepercutedDispatch = {
    setCustomers: (payload: CustomerPayload) => ({ type: 'CHANGE_CUSTOMER', payload }),
    setWorkingCenters: (payload: WorkingCenterPayload) => ({ type: 'CHANGE_WORKINGCENTER', payload }),
}
const mapTableVatRepercutedConnector = (state: AllReduxPayloads) => ({ customers: state.customers, workingCenters: state.workingCenters })
const tableVatRepercutedConnector = connect(mapTableVatRepercutedConnector, tableVatRepercutedDispatch)
type ReduxVatRepercutedConnector = ConnectedProps<typeof tableVatRepercutedConnector>

const tableVatRepercutedConnectLoading = withLoading(TableVatRepercuted, [{ fetchUrl: '/api/app/workingCenter/getAllWorkingCentersComplete', propName: 'workingCenters', setFunctionName: 'setWorkingCenters' }, { fetchUrl: '/api/app/customer/getAllCustomersComplete', propName: 'customers', setFunctionName: 'setCustomers' }])

const TableVatRepercutedConnect = tableVatRepercutedConnector(tableVatRepercutedConnectLoading)

export default VatRepercuted